import { useState } from "react";
import Marquee from "react-fast-marquee";
import { BIRDS } from "@/lib/brand";
import { useLang } from "@/lib/i18n";
import CaseStudyDialog from "./CaseStudyDialog";

const CLIENTS_ROW_1 = [
  { name: "Royal Enfield", sector: "Automotive" },
  { name: "Mahindra", sector: "Automotive" },
  { name: "Suzuki", sector: "Automotive" },
  { name: "Sanjeev Auto", sector: "Automotive" },
  { name: "Reliance Digital", sector: "Retail" },
  { name: "Apple Showroom", sector: "Retail" },
  { name: "Arriston Luxury", sector: "Luxury" },
  { name: "Vail Perfume", sector: "Luxury" },
];

const CLIENTS_ROW_2 = [
  { name: "ITC Hotel Rama", sector: "Hospitality" },
  { name: "Spree Hotel", sector: "Hospitality" },
  { name: "Te Amo", sector: "F&B" },
  { name: "Medicover Hospitals", sector: "Healthcare" },
  { name: "Central Bank of India", sector: "Banks" },
  { name: "Vardhaman Bank", sector: "Banks" },
  { name: "Shamit School", sector: "Education" },
  { name: "Deogiri Global School", sector: "Education" },
  { name: "Endurance", sector: "Industry" },
  { name: "Varun Beverages", sector: "Industry" },
];

function Row({ items, reverse = false, useBirds = false, onSelect }) {
  return (
    <Marquee
      speed={reverse ? 28 : 34}
      direction={reverse ? "right" : "left"}
      gradient={false}
      pauseOnHover
      className="py-4"
    >
      {items.map((c, i) => (
        <button
          type="button"
          key={`${c.name}-${i}`}
          onClick={() => onSelect(c.name)}
          data-testid={`marquee-item-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
          className="group flex items-center gap-6 md:gap-10 px-6 md:px-10 whitespace-nowrap cursor-pointer text-left focus:outline-none"
        >
          <img
            src={useBirds ? BIRDS.bird_pop : BIRDS.bird_mark}
            alt=""
            aria-hidden
            className="w-6 h-6 md:w-8 md:h-8 object-contain opacity-90 transition-transform duration-500 group-hover:-translate-y-1"
          />
          <div>
            <div className="font-mono text-[9px] md:text-[10px] tracking-[0.3em] uppercase text-black/50">
              {c.sector}
            </div>
            <div className="font-display italic text-4xl md:text-6xl leading-none tracking-tight transition-all duration-500 group-hover:not-italic">
              {c.name}
            </div>
          </div>
          <span className="text-black/25 font-display text-6xl md:text-8xl leading-none">
            ×
          </span>
        </button>
      ))}
    </Marquee>
  );
}

export default function ClientsMarquee() {
  const { t } = useLang();
  const [openBrand, setOpenBrand] = useState(null);
  const [open, setOpen] = useState(false);

  const select = (name) => {
    setOpenBrand(name);
    setOpen(true);
  };

  return (
    <section
      id="clients"
      data-testid="clients-section"
      className="relative bg-[#F4F4F0] text-black border-t border-black overflow-hidden"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 pt-24 md:pt-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-8">
              <span className="w-10 h-px bg-black" />
              <span className="font-mono text-[10px] tracking-[0.35em] uppercase">
                {t("clients.chapter")}
              </span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
              {t("clients.title_1")} <br />
              <em className="italic">{t("clients.title_2_italic")}</em>{" "}
              {t("clients.title_3")}
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex flex-col justify-end gap-3">
            <p className="text-base md:text-lg text-black/75 leading-relaxed max-w-[52ch]">
              {t("clients.sub")}
            </p>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--ap-magenta)]">
              → {t("clients.tap_hint")}
            </div>
          </div>
        </div>
      </div>

      <div className="border-y border-black">
        <Row items={[...CLIENTS_ROW_1, ...CLIENTS_ROW_1]} onSelect={select} />
      </div>
      <div className="border-b border-black bg-[#0A0A0A] text-[#F4F4F0]">
        <div className="[&_.font-display]:text-[#F4F4F0] [&_.text-black\/50]:text-[#F4F4F0]/60 [&_.text-black\/25]:text-[#F4F4F0]/25">
          <Row
            items={[...CLIENTS_ROW_2, ...CLIENTS_ROW_2]}
            reverse
            useBirds
            onSelect={select}
          />
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-10 md:py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/60">
          {t("clients.sectors")}
        </div>
        <a href="#contact" className="btn-offset" data-testid="clients-cta">
          {t("clients.cta")}
        </a>
      </div>

      <CaseStudyDialog
        brandName={openBrand}
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setOpenBrand(null);
        }}
      />
    </section>
  );
}
